import 'reflect-metadata';

/**
 * Gets the Decorator strings from the shell strings on the fly
 */
export function getDecoratorStrings() {
    const decoratorStrings = {
        Deprecated: {
            messageFormat: '\"{0}\" has been deprecated since {1}. {2} {3}',
            alternateSignatureFormat: 'Use \"{0}\" instead.'
        },
        Obsolete: {
            messageFormat: '\"{0}\" has been obsolete since {1} and will be removed soon. {2} {3}',
            alternateSignatureFormat: 'Use \"{0}\" instead.'
        }
    };
    return decoratorStrings;
}

/**
 * Describes a function used to modify an instance of a class before its constructor is called
 */
export type InstanceModifierFunction =
    <TFunction extends Function>(instance: any, originalConstructor: TFunction, ...args: any[]) => void;

/**
 * Describes a function used to modify an instance of a class before its constructor is called
 */
export type ClassModifierFunction =
    <TFunction extends Function>(newConstructor: TFunction, originalConstructor: TFunction) => void;

/**
 * Universal Decorator Options
 */
export interface UniversalDecoratorOptions {
    /**
     * The name of this decorator
     */
    name: string;
    /**
     * The decorator to use if a class decorator is needed
     */
    classDecorator?: ClassDecorator;
    /**
     * The decorator to use if a property decorator is needed
     */
    propertyDecorator?: PropertyDecorator;
    /**
     * The decorator to use if a accessor decorator is needed
     */
    accessorDecorator?: MethodDecorator;
    /**
     * The decorator to use if a method decorator is needed
     */
    methodDecorator?: MethodDecorator;
    /**
     * The decorator to use if a parameter decorator is needed
     */
    parameterDecorator?: ParameterDecorator;
}

/**
 * Generic definition for any Decorator type
 */
export type Decorator = ClassDecorator | PropertyDecorator | MethodDecorator | ParameterDecorator;

/**
 * Enables the creation of a decorator that in turn can be used for any decorator type provided in the options
 * @param options The options for this decorator
 */
export function createUniversalDecorator(options: UniversalDecoratorOptions): any {
    return function (target: any, property: string | symbol, descriptorOrParameterIndex: PropertyDescriptor | number) {
        // Only class decorators have 1 argument
        if (arguments.length === 1) {
            if (!options.classDecorator) {
                throw new SyntaxError(`${options.name} decorator is not supported on classes.`);
            }
            return options.classDecorator(target);
        }

        // Only property decorators have no third argument.
        if (descriptorOrParameterIndex === undefined) {
            if (!options.propertyDecorator) {
                throw new SyntaxError(`${options.name} decorator is not supported on properties.`);
            }
            return options.propertyDecorator(target, property);
        }

        // Parameter decorators have a nuumber as the third argument.
        if (MsftSme.isNumber(descriptorOrParameterIndex)) {
            if (!options.parameterDecorator) {
                throw new SyntaxError(`${options.name} decorator is not supported on parameters.`);
            }
            return options.parameterDecorator(target, property, descriptorOrParameterIndex as number);
        }

        // now we know that the third argument must be a descriptor
        const descriptor = descriptorOrParameterIndex as PropertyDescriptor;

        // Method descriptors have a value
        if (MsftSme.isFunction(descriptor.value)) {
            if (!options.methodDecorator) {
                throw new SyntaxError(`${options.name} decorator is not supported on methods.`);
            }
            return options.methodDecorator(target, property, descriptor);
        }

        // Accessor descriptors have a get/set
        if (MsftSme.isFunction(descriptor.get) || MsftSme.isFunction(descriptor.set)) {
            if (!options.accessorDecorator) {
                throw new SyntaxError(`${options.name} decorator is not supported on get/set accessors.`);
            }
            return options.accessorDecorator(target, property, descriptor);
        }

        // if we get here something terrible has happened and we shall be scratching our heads.
        const type = MsftSme.getValue(target, 'logSourceName')
            || MsftSme.getValue(target, 'name')
            || MsftSme.getValue(target, 'constructor.name')
            || property
            || 'unknown type';
        throw new SyntaxError(`${options.name} decorator target not supported on ${type}.`);
    };
}
